<template>
  <div class="merchant_centre_wizard">
    <div class="setup_steps">
      <h3>Merchant setup</h3>

      <ul>
        <li :class="stepStatusClass([1, 2])">
          <div class="step_number">
            <span v-if="!isStepComplete(2)">1</span>
            <svg-vue
              v-else
              icon="icons/checkmark"
            />
          </div>
          <span>
            Set up custom domain<br>
            <small v-html="isStepComplete(2) ? 'Complete' : '(10 minutes)'" />
          </span>
        </li>
        <li :class="stepStatusClass([3, 4])">
          <div class="step_number">
            <span v-if="!isStepComplete(4)">2</span>
            <svg-vue
              v-else
              icon="icons/checkmark"
            />
          </div>
          <span>
            Set up custom email<br>
            <small v-html="isStepComplete(4) ? 'Complete' : '(5 minutes)'" />
          </span>
        </li>
        <li :class="stepStatusClass([5])">
          <div class="step_number">
            <span v-if="!isStepComplete(5)">3</span>
            <svg-vue
              v-else
              icon="icons/checkmark"
            />
          </div>
          <span>
            Create Stripe account<br>
            <small v-html="isStepComplete(5) ? 'Complete' : '(15 minutes)'" />
          </span>
        </li>
        <li :class="stepStatusClass([6])">
          <div class="step_number">
            <span v-if="!isStepComplete(6)">4</span>
            <svg-vue
              v-else
              icon="icons/checkmark"
            />
          </div>
          <span>
            Set up taxes<br>
            <small v-html="isStepComplete(6) ? 'Complete' : '(2 minutes)'" />
          </span>
        </li>
        <li :class="stepStatusClass([7])">
          <div class="step_number">
            <span v-if="!isStepComplete(7)">5</span>
            <svg-vue
              v-else
              icon="icons/checkmark"
            />
          </div>
          <span>
            Set up pricing<br>
            <small v-html="isStepComplete(8) ? 'Complete' : '(10 minutes)'" />
          </span>
        </li>
        <li :class="stepStatusClass([8])">
          <div class="step_number">
            <span v-if="!isStepComplete(8)">6</span>
            <svg-vue
              v-else
              icon="icons/checkmark"
            />
          </div>
          <span>
            Create add-ons<br>
            <small v-html="isStepComplete(8) ? 'Complete' : '(5 minutes)'" />
          </span>
        </li>
        <li :class="stepStatusClass([9])">
          <div class="step_number">
            <span v-if="!isStepComplete(9)">7</span>
            <svg-vue
              v-else
              icon="icons/checkmark"
            />
          </div>
          <span>
            Set up checkout page<br>
            <small v-html="isStepComplete(9) ? 'Complete' : '(5 minutes)'" />
          </span>
        </li>
        <li :class="stepStatusClass([10])">
          <div class="step_number">
            <span v-if="!isStepComplete(10)">8</span>
            <svg-vue
              v-else
              icon="icons/checkmark"
            />
          </div>
          Ready to go!
        </li>
      </ul>
    </div>

    <div class="setup_form">
      <a
        v-if="canGoBack"
        class="go_back"
        href="javascript:;"
        @click="goBack"
        v-html="backButtonText"
      />

      <custom-domain v-if="currentStep === 'CUSTOM_DOMAIN'" />
      <custom-domain-setup v-if="currentStep === 'CUSTOM_DOMAIN_SETUP'" />
      <custom-email v-if="currentStep === 'CUSTOM_EMAIL'" />
      <custom-email-setup v-if="currentStep === 'CUSTOM_EMAIL_SETUP'" />
      <stripe-account v-if="currentStep === 'STRIPE_ACCOUNT'" />
      <tax-setup v-if="currentStep === 'TAX'" />
      <custom-products v-if="currentStep === 'PRODUCTS'" />
      <custom-addons v-if="currentStep === 'ADDONS'" />
      <merchant-registration v-if="currentStep === 'REGISTRATION'" />
      <ready-to-go v-if="currentStep === 'READY_TO_GO'" />
    </div>
  </div>
</template>

<script>
  import { mapActions, mapGetters } from "vuex";
  import CustomDomain from "./setupWizardSteps/CustomDomain";
  import CustomDomainSetup from "./setupWizardSteps/CustomDomainSetup";
  import CustomEmail from "./setupWizardSteps/CustomEmail";
  import CustomEmailSetup from "./setupWizardSteps/CustomEmailSetup";
  import StripeAccount from "./setupWizardSteps/StripeAccount";
  import TaxSetup from "./setupWizardSteps/TaxSetup";
  import CustomProducts from "./setupWizardSteps/CustomProducts";
  import CustomAddons from "./setupWizardSteps/CustomAddons";
  import MerchantRegistration from "./setupWizardSteps/MerchantRegistration";
  import ReadyToGo from "./setupWizardSteps/ReadyToGo";
  import _ from "lodash";

  export default {
    components: {
      CustomDomain,
      CustomDomainSetup,
      CustomEmail,
      CustomEmailSetup,
      StripeAccount,
      TaxSetup,
      CustomProducts,
      CustomAddons,
      MerchantRegistration,
      ReadyToGo,
    },
    computed: {
      ...mapGetters('merchantCentreSetupWizard', [
        'allSteps',
        'currentStep',
        'formFields',
        'whitelabelStatus',
      ]),
      canGoBack() {
        const currentStepIndex = _.indexOf(this.allSteps, this.currentStep) + 1;
        return currentStepIndex > 6
          && currentStepIndex < 10;
      },
      backButtonText() {
        if (this.currentStep === 'PRODUCTS') {
          return '&lt; Return to tax configuration'
        }
        if (this.currentStep === 'ADDONS') {
          return '&lt; Return to product configuration'
        }
        if (this.currentStep === 'REGISTRATION') {
          return '&lt; Return to add-on configuration'
        }

        return '&lt; Back'
      }
    },
    methods: {
      ...mapActions('merchantCentreSetupWizard', [
        'fetchWhitelabelStatus',
        'previousStep',
      ]),
      isStepActive(steps) {
        const currentStepIndex = _.indexOf(this.allSteps, this.currentStep) + 1;
        return steps.includes(currentStepIndex);
      },
      isStepComplete(stepNumber) {
        const currentStepIndex = _.indexOf(this.allSteps, this.currentStep) + 1;
        return currentStepIndex > stepNumber;
      },
      stepStatusClass(stepsCovered) {
        let statusClass = '';
        const lastStepIndex = stepsCovered[stepsCovered.length-1];

        if (this.isStepActive(stepsCovered)) {
          statusClass = 'active';
        }
        if (this.isStepComplete(lastStepIndex)) {
          statusClass = 'active complete';
        }

        return statusClass;
      },
      goBack() {
        this.previousStep();
      },
    },
  }
</script>

<style lang="scss">
  .page-container .main-content {
    padding-bottom: 0!important;
  }

  .merchant_centre_wizard {
    display: flex;
    flex-grow: 1;
    width: 100vw;
    height: calc(100vh - 66px);
    overflow: hidden;
    margin-left: -30px;
    margin-top: -30px;

    .form-control {
      margin: 0 0 0.5rem 0;
      border: 1px solid #E8E5E2;
      border-radius: 12px;

      &.error {
        border-color: #d47777;
      }
    }

    .form-group {
      margin-bottom: 1.5rem;

      &:last-of-type {
        margin-bottom: .5rem;
      }

      label {
        font-size: 1.125rem;
        line-height: 1.625rem;
        font-weight: 500;
        margin-bottom: .5rem;
      }

      small {
        font-size: 0.75rem;
        letter-spacing: -0.1px;
      }
    }

    .setup_steps {
      max-width: 285px;
      background: #E8E5E2;

      > h3 {
        padding: 1.5rem;
        font-size: 1.125rem;
        font-weight: 500;
        margin: 0;
      }

      .step-description {
        font-size: .875rem;
        line-height: 1.375rem;
        letter-spacing: -0.1px;
      }

      ul {
        padding: 0;
        list-style: none;

        li {
          padding: 0.75rem 1.5rem;
          font-size: 0.875rem;
          font-weight: 700;
          color: #040707;
          white-space: nowrap;
          display: flex;
          align-items: center;

          .step_number {
            border: 1px solid #040707;
            border-radius: 100%;
            width: 24px;
            height: 24px;
            text-align: center;
            font-size: 0.875rem;
            font-weight: 700;
            margin-right: 1rem;

            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            padding: 8px;
            gap: 10px;

            svg {
              flex: none;
              order: 0;
              flex-grow: 0;
              height: 16px;
              width: 12px;

              path {
                stroke: white;
              }
            }
          }

          span {
            line-height: 1rem;
          }

          &.active {
            background: white;

            .step_number {
              background: #040707;
              color: white;
            }

            &.complete {
              background: none;
              color: #909096;

              .step_number {
                background: #909096;
                color: #E8E5E2;
                border-color: #E8E5E2;
              }
            }
          }
        }
      }
    }

    .setup_form {
      width: 100%;
      overflow: auto;
      padding: 2rem;
      display: flex;
      flex-direction: column;

      .setup_step {
        margin: 2rem auto;
        width: 100%;
        max-width: 690px;
        padding: 2.5rem;
        box-shadow: 0 10px 30px rgba(4, 7, 7, 0.08);

        > h4 {
          font-size: 2.5rem;
          line-height: 3.125rem;
          font-weight: 700;
          text-align: center;
          margin-bottom: 1rem;
        }

        > p {
          font-size: 0.875rem;
          color: #4E4E54;
          text-align: center;
          margin-bottom: 1.5rem;
        }

        .button {
          background: var(--primary-color);
          border-radius: 12px;
          padding: 1rem;
          color: #fff;
          font-size: 1.5rem;
          line-height: 2.125rem;
          font-weight: 700;
          transition: .3s;

          &:hover {
            background: var(--primary-color-dark);
          }

          &.info {
            border-radius: 6px;
            align-self: center;
            background: white;
            color: #6ECADA;
            padding: .5rem .75rem;
            border: 1px solid #6ECADA;
            font-weight: 700;
            font-size: .75rem;
            line-height: 1rem;
            margin-bottom: 2rem;

            > svg {
              width: 1rem;
              height: 1rem;
              margin-top: -2px;

              path {
                stroke: #6ECADA;

                &[fill] {
                  fill: #6ECADA;
                }
              }
            }
          }

          &.error {
            background-color: #d47777;
            border-color: #d47777;

            &:hover {
              background-color: #ff568f;
              border-color: #ff568f;
            }
          }
        }

        .temp_modal {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0, 0, 0, 0.3);
          padding: 130px 4rem 4rem;
          display: flex;
          align-items: center;

          .inner {
            background: white;
            border-radius: 12px;
            box-shadow: 0 10px 30px rgba(4, 7, 7, 0.08);
            padding: 2rem;
            width: 100%;

            iframe {
              height: 75vh;
              overflow: hidden;
            }
          }
        }

        small {
          font-size: 0.75rem;
          color: #4E4E54;
        }
      }

      .go_back {
        font-size: 16px;
        color: black;
        border-bottom: 1px solid black;
        align-self: center;
      }
    }
  }
</style>
